import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, PhoneOff, Video } from "lucide-react";
import { getConsultationAccess } from "@/utils/consultationAccess";

type CallType = "audio" | "video";

interface IncomingCallInfo {
  conversationId: string;
  patientId: string;
  callerName: string;
  callType: CallType;
}

interface GlobalCallListenerProps {
  practitionerType: "dietitian" | "doctor";
}

const GlobalCallListener = ({ practitionerType }: GlobalCallListenerProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [incomingCall, setIncomingCall] = useState<IncomingCallInfo | null>(null);
  const channelsRef = useRef<Map<string, ReturnType<typeof supabase.channel>>>(new Map());
  const convMapRef = useRef<Map<string, string>>(new Map());
  const convAccessRef = useRef<Map<string, {
    status: string;
    paid: boolean;
    created_at: string;
    consultation_started_at: string | null;
    consultation_expires_at: string | null;
    chat_unlocked_until: string | null;
  }>>(new Map());
  const userIdRef = useRef<string | null>(null);
  // Keep pathname in a ref so channel callbacks always see the latest value
  const pathnameRef = useRef(location.pathname);

  useEffect(() => { pathnameRef.current = location.pathname; }, [location.pathname]);
  useEffect(() => { userIdRef.current = user?.id ?? null; }, [user?.id]);

  useEffect(() => {
    if (!user) return;

    const load = async () => {
      const { data: convData } = await supabase
        .from("conversations")
        .select("id, patient_id, status, paid, created_at, consultation_started_at, consultation_expires_at, chat_unlocked_until")
        .eq("practitioner_id", user.id)
        .eq("practitioner_type", practitionerType)
        .eq("status", "accepted");

      const openConversations = (convData ?? []).filter((conversation) =>
        getConsultationAccess(conversation) === "open",
      );
      if (openConversations.length === 0) return;

      openConversations.forEach((c) => {
        convMapRef.current.set(c.id, c.patient_id);
        convAccessRef.current.set(c.id, c);
      });

      openConversations.forEach((c) => {
        if (channelsRef.current.has(c.id)) return;

        const channel = supabase.channel(`call-${c.id}`, {
          config: { broadcast: { self: false } },
        });

        channel.on("broadcast", { event: "signal" }, ({ payload }) => {
          const { type, senderId, callerName, callType: ct } = payload as Record<string, string>;
          if (senderId === userIdRef.current) return;

          if (type === "call-request") {
            const access = convAccessRef.current.get(c.id);
            if (!access || getConsultationAccess(access) !== "open") return;
            const patientId = convMapRef.current.get(c.id);
            if (!patientId) return;

            // Use the ref so we always check the current pathname, not a stale closure
            const currentPath = pathnameRef.current;
             const onThatChatPage = currentPath.includes(`/chat/${c.id}`);
            if (onThatChatPage) return;

            setIncomingCall({
              conversationId: c.id,
              patientId,
              callerName,
              callType: ct as CallType,
            });
          }

          if (type === "call-ended" || type === "call-rejected") {
            setIncomingCall((prev) => (prev?.conversationId === c.id ? null : prev));
          }
        });

        channel.subscribe();
        channelsRef.current.set(c.id, channel);
      });
    };

    load();

    return () => {
      channelsRef.current.forEach((ch) => supabase.removeChannel(ch));
      channelsRef.current.clear();
      convMapRef.current.clear();
      convAccessRef.current.clear();
    };
  }, [user, practitionerType]);

  // Clear notification when practitioner navigates to that patient's chat
  useEffect(() => {
    if (!incomingCall) return;
    if (location.pathname.includes(`/chat/${incomingCall.patientId}`)) {
      setIncomingCall(null);
    }
  }, [location.pathname, incomingCall]);

  const handleAccept = () => {
    if (!incomingCall) return;
    navigate(`/${practitionerType}/chat/${incomingCall.conversationId}`, {
      state: { autoAnswer: true, callType: incomingCall.callType },
    });
    setIncomingCall(null);
  };

  const handleReject = () => {
    if (!incomingCall || !userIdRef.current) return;
    const channel = channelsRef.current.get(incomingCall.conversationId);
    channel?.send({
      type: "broadcast",
      event: "signal",
      payload: { type: "call-rejected", senderId: userIdRef.current },
    });
    setIncomingCall(null);
  };

  const isOnChatPage = location.pathname.includes("/chat/");

  return (
    <AnimatePresence>
      {incomingCall && !isOnChatPage && (
        <motion.div
          className="fixed inset-0 z-[200] bg-gray-950/95 flex flex-col items-center justify-center gap-8 p-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="w-24 h-24 rounded-full bg-primary/20 border-4 border-primary flex items-center justify-center"
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ repeat: Infinity, duration: 1.4 }}
          >
            <span className="text-4xl font-extrabold text-primary">
              {(incomingCall.callerName || "?").charAt(0).toUpperCase()}
            </span>
          </motion.div>

          <div className="text-center">
            <p className="text-white text-xl font-extrabold">{incomingCall.callerName}</p>
            <p className="text-white/50 text-sm mt-1.5">
              Incoming {incomingCall.callType === "video" ? "video" : "voice"} call…
            </p>
          </div>

          <div className="flex gap-12">
            <div className="flex flex-col items-center gap-2">
              <button
                onClick={handleReject}
                className="h-16 w-16 rounded-full bg-red-500 flex items-center justify-center shadow-lg active:scale-95 transition-transform"
              >
                <PhoneOff className="h-7 w-7 text-white" />
              </button>
              <span className="text-white/50 text-xs">Decline</span>
            </div>

            <div className="flex flex-col items-center gap-2">
              <motion.button
                onClick={handleAccept}
                animate={{ scale: [1, 1.08, 1] }}
                transition={{ repeat: Infinity, duration: 1.2 }}
                className="h-16 w-16 rounded-full bg-green-500 flex items-center justify-center shadow-lg active:scale-95"
              >
                {incomingCall.callType === "video" ? (
                  <Video className="h-7 w-7 text-white" />
                ) : (
                  <Phone className="h-7 w-7 text-white" />
                )}
              </motion.button>
              <span className="text-white/50 text-xs">Accept</span>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GlobalCallListener;
